import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import StaticPageLayout from '../../components/StaticPageLayout';
import { useThemeStore } from '../../store/theme';

const gettingStartedLinks = [
  { title: 'What is SweetPilot?', href: '/doc/article/what-is-sweetpilot' },
  { title: 'Plans and pricing', href: '/doc/article/pricing-and-plan' },
  { title: 'Login to SweetPilot', href: '/doc/article/login-to-sweetpilot' },
  { title: 'How to get started with SweetPilot', href: '/doc/article/getting-started' },
];

const navigatingAppsLinks = [
  { title: 'How to navigate the SweetPilot web app', href: '/doc/article/navigate-web-app' },
  { title: 'What languages is the UI (Interface) available in?', href: '/doc/article/ui-languages' },
];

const generalInfoLinks = [
  { title: 'Can I get a demo or a free trial?', href: '/doc/article/demo-free-trial' },
  { title: 'Which AI Models does SweetPilot use?', href: '/doc/article/ai-models' },
  { title: 'Multiple language support in SweetPilot App', href: '/doc/article/multi-language-support' },
  { title: 'Understanding AI Hallucinations', href: '/doc/article/ai-hallucinations' },
  { title: 'Who can benefit from using SweetPilot?', href: '/doc/article/who-benefits' },
  { title: 'SweetPilot AI Legacy products', href: '/doc/collections/legacy-products', articles: 3 },
];

const videoTutorialsLinks = [
  { title: 'Onboarding to SweetPilot (tutorial)', href: '/doc/article/onboarding-tutorial' },
  { title: 'SweetPilot Brain AI (tutorial)', href: '/doc/article/brain-ai-tutorial' },
  { title: 'Schedule Posts with SweetPilot Social Media Manager (tutorial)', href: '/doc/article/social-media-manager-tutorial' },
];

interface LinkItemProps {
  title: string;
  href: string;
  articles?: number;
  isDark: boolean;
}

function LinkItem({ title, href, articles, isDark }: LinkItemProps) {
  return (
    <Link
      to={href}
      className={`flex items-center justify-between py-4 px-2 transition-all group border-b ${
        isDark
          ? 'border-white/5 hover:bg-white/5'
          : 'border-slate-100 hover:bg-slate-50'
      }`}
    >
      <div>
        <span className={`font-medium transition-colors ${
          isDark ? 'text-white group-hover:text-purple-300' : 'text-slate-900 group-hover:text-purple-600'
        }`}>
          {title}
        </span>
        {articles && (
          <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
            {articles} articles
          </p>
        )}
      </div>
      <ChevronRight className={`w-5 h-5 transition-colors ${
        isDark ? 'text-gray-600 group-hover:text-purple-400' : 'text-slate-300 group-hover:text-purple-600'
      }`} />
    </Link>
  );
}

interface SectionCardProps {
  title: string;
  links: Array<{ title: string; href: string; articles?: number }>;
  isDark: boolean;
  delay?: number;
}

function SectionCard({ title, links, isDark, delay = 0 }: SectionCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className={`rounded-2xl border overflow-hidden ${
        isDark
          ? 'bg-dark-800/50 border-white/5'
          : 'bg-white border-slate-200 shadow-sm'
      }`}
    >
      <div className={`px-6 py-5 border-b ${isDark ? 'border-white/5' : 'border-slate-100'}`}>
        <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{title}</h2>
      </div>
      <div className="px-4">
        {links.map((link) => (
          <LinkItem
            key={link.title}
            title={link.title}
            href={link.href}
            articles={link.articles}
            isDark={isDark}
          />
        ))}
      </div>
    </motion.div>
  );
}

export default function GettingStarted() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  return (
    <StaticPageLayout
      title=""
      subtitle=""
      showBackButton={false}
    >
      {/* Breadcrumbs */}
      <nav className="mb-8">
        <ol className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
          <li>
            <Link
              to="/docs"
              className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}
            >
              All Collections
            </Link>
          </li>
          <li>
            <ChevronRight className="w-4 h-4" />
          </li>
          <li className={isDark ? 'text-white' : 'text-slate-900'}>
            Getting started
          </li>
        </ol>
      </nav>

      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-10"
      >
        <h1 className={`text-4xl font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
          Getting started
        </h1>
        <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
          Learn how to get started with SweetPilot.
        </p>
      </motion.div>

      {/* Main Getting Started Links */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className={`rounded-2xl border overflow-hidden mb-6 ${
          isDark
            ? 'bg-dark-800/50 border-white/5'
            : 'bg-white border-slate-200 shadow-sm'
        }`}
      >
        <div className="px-4">
          {gettingStartedLinks.map((link) => (
            <LinkItem
              key={link.title}
              title={link.title}
              href={link.href}
              isDark={isDark}
            />
          ))}
        </div>
      </motion.div>

      {/* Navigating SweetPilot AI apps */}
      <SectionCard
        title="Navigating SweetPilot AI apps"
        links={navigatingAppsLinks}
        isDark={isDark}
        delay={0.2}
      />

      <div className="h-6" />

      {/* General info */}
      <SectionCard
        title="General info"
        links={generalInfoLinks}
        isDark={isDark}
        delay={0.3}
      />

      <div className="h-6" />

      {/* Video tutorials */}
      <SectionCard
        title="Video tutorials"
        links={videoTutorialsLinks}
        isDark={isDark}
        delay={0.4}
      />

      <div className="h-16" />

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-center"
      >
        <div className={`w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center ${
          isDark ? 'bg-purple-500/20' : 'bg-purple-100'
        }`}>
          <span className="text-2xl">🤖</span>
        </div>
        <a
          href="mailto:help@sweetpilot.com"
          className={`text-sm transition-colors ${
            isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'
          }`}
        >
          help@sweetpilot.com
        </a>
      </motion.div>
    </StaticPageLayout>
  );
}
